import { useContext, useEffect, useMemo, useState } from "react";
import {
  Text,
  Card,
  Stack,
  TextInput,
  Title,
  Container,
  Grid,
  Divider,
  Select,
  Checkbox,
} from "@mantine/core";
import { UseFormReturnType, useForm } from "@mantine/form";

import { collection, getDocs, } from "firebase/firestore";

import { ConfigContext, } from "../../contexts";

import { AddServiceRequest, UpdateLink, PortfolioType, PortfolioOpeningRequest, PortfolioEntry, ManagementOption, RiskLevel, Portfolio } from "../../types";

import { useFirestore } from "../../helpers/firebaseContext";

import { GenevaApiSelect } from "../../components/GenevaApiSelect";

import BaseServiceForm from "../base_service/BaseServiceForm";


const portfolioTypeGenevaMap = (portfolioType: PortfolioType) => {
  switch (portfolioType) {
    case "custody":
      return "Custody";
    case "dpm":
      return "DPM";
    case "non-dpm":
      return "NonDPM";
    default:
      return "Other";
  }
};
const AddPortfolio = (props: any) => {

  const config = useContext(ConfigContext);
  const db = useFirestore();

  const [portfolioKey, setPortfolioKey] = useState<string | null>(null);
  const [managementOptionType, setManagementOptionType] = useState<string | null>(null);

  const [existingPortfolio, setExistingPortfolio] = useState<Portfolio | undefined>(undefined)
  const [feesManuallyEdited, setFeesManuallyEdited] = useState(false);


  // --- derived data ---
  const portfolioObj: PortfolioEntry | null = useMemo(() => {
    if (!portfolioKey || !config.portfolio_service) return null;
    return config.portfolio_service[portfolioKey] ?? null;
  }, [portfolioKey, config]);

  const managementOptions: ManagementOption[] = useMemo(() => {
    return portfolioObj?.management_options ?? [];
  }, [portfolioObj]);

  const selectedManagementOption: ManagementOption | null = useMemo(() => {
    if (!managementOptionType) return null;
    return (
      managementOptions.find((m) => m.portfolio_type === managementOptionType) ?? null
    );
  }, [managementOptionType, managementOptions]);

  // --- handlers ---
  const handlePortfolioChange = (value: string | null) => {
    setPortfolioKey(value);
    const foundPortfolio = portfolioSelectData.find((portfolioObj) => portfolioObj.value === value)
    form.setFieldValue("updateLink.selectedWhatsappTemplateID", foundPortfolio?.whatsappTemplate);
    // reset downstream when parent changes
    setManagementOptionType(null);
  };

  const handleManagementOptionChange = (value: string | null) => {
    setManagementOptionType(value);
  };

  // --- select data sources ---
  // 1. portfolio select: list every key in PortfolioService
  const portfolioSelectData = useMemo(() => {
    return Object.entries(config.portfolio_service ?? {})
      // you can filter inactive if you want: .filter(([_, p]) => p.active !== false)
      .sort((a, b) => {
        const pa = a[1].position ?? 0;
        const pb = b[1].position ?? 0;
        return pa - pb;
      })
      .map(([key, portfolio]) => ({
        value: key,
        label: portfolio.name_en || key,
        description: portfolio.description_en,
        whatsappTemplate: portfolio.whatsappTemplate,
      }));
  }, [config.portfolio_service]);

  // 2. management option select: from chosen portfolio
  const managementOptionSelectData = useMemo(() => {
    return managementOptions.map((m) => ({
      value: m.portfolio_type,
      label: m.portfolio_type,
      description: m.description_en,
    }));
  }, [managementOptions]);

  // --- hide selects when not needed ---
  const shouldShowManagementSelect = managementOptions.length > 1;

  // If only one management option, auto-select it
  useEffect(() => {
    if (
      portfolioObj &&
      managementOptions.length === 1 &&
      managementOptionType == null
    ) {
      setManagementOptionType(managementOptions[0].portfolio_type);
    }
  }, [portfolioObj, managementOptions, managementOptionType]);

  const form: UseFormReturnType<AddServiceRequest> = useForm<AddServiceRequest>({
    initialValues: {
      updateLink: {
        smsDocID: "KFHCapital"
      } as UpdateLink,
      signingMethod: "paci",
      onBehalfOf: undefined,
      action: "send_service_sms",
      service: {
        serviceType: "portfolio",
        portfolioOpeningRequest: {
          portfolio_db_id: "",
          portfolioType: selectedManagementOption?.portfolio_type || "",
          portfolioId: "",
          fees: selectedManagementOption?.fees || 0,
          assetClass: selectedManagementOption?.asset_class || ""
        } as PortfolioOpeningRequest
      }
    },

    validate: {
      service: {
        kycLinkWhatsappNumber: (value, values) => {
          if (values.service.customerObj?.commercial_register && !value) {
            return 'KYC Link Whatsapp Number is required for company customers';
          }
          return null;
        },
      },
    },
  });



  // Add this useEffect after your other useEffects
  useEffect(() => {
    const fetchMatchingPortfolio = async () => {
      const customerID = form.values.service.customerObj?.id
      if (selectedManagementOption?.portfolio_type && customerID) {
        try {
          // Access subcollection: /customers/{customerID}/portfolios
          const portfoliosRef = collection(db, 'customers', customerID, 'portfolios');
          const querySnapshot = await getDocs(portfoliosRef);

          // Find the matching portfolio document
          const matchingPortfolioDoc = querySnapshot.docs.find(
            (doc) => doc.data().status?.toLowerCase() === 'active' && doc.data().portfolioTypeCode === portfolioTypeGenevaMap(selectedManagementOption.portfolio_type.toLowerCase() as PortfolioType)
          );

          // Todo check with the CR team logic for existing portfolio. When multiple portfolios exist
          if (matchingPortfolioDoc) {
            const portfolioData = {
              id: matchingPortfolioDoc.id,
              ...matchingPortfolioDoc.data()
            } as Portfolio;

            console.log('Matching portfolio found:', portfolioData);
            setExistingPortfolio(portfolioData);
          } else {
            console.log('No matching portfolio found');
            setExistingPortfolio(undefined);
          }
        } catch (error) {
          console.error('Error fetching portfolios from Firestore:', error);
          setExistingPortfolio(undefined);
        }
      } else {
        // Reset portfolio if either dependency is null
        setExistingPortfolio(undefined);
      }
    };

    fetchMatchingPortfolio();
  }, [selectedManagementOption, form.values.service.customerObj?.id]);


  // Only auto-update fees if user hasn't manually edited them
  useEffect(() => {
    if (selectedManagementOption?.fees !== undefined &&
      form.values.service.portfolioOpeningRequest &&
      !feesManuallyEdited) {
      form.setFieldValue("service.portfolioOpeningRequest.fees", selectedManagementOption.fees);
    }
  }, [selectedManagementOption?.fees, feesManuallyEdited]);

  // Reset the manual edit flag when management option changes
  useEffect(() => {
    setFeesManuallyEdited(false);
  }, [selectedManagementOption]);

  useEffect(() => {
    if (!form.values.service.customerObj?.commercial_register) {
      form.setFieldValue("service.kycLinkWhatsappNumber", '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.service.customerObj?.id]);

  const preSubmitFormValues = (formValues: AddServiceRequest) => {

    console.log("FORM VALUES SUBMITTED ARE  =====>", formValues);
    const formValuesToBeSent = { ...formValues };  //I'm making a copy because I might modify things here.  I don't want the form to reflect those changes.
    formValuesToBeSent.service = { ...formValues.service };  //same
    formValuesToBeSent.updateLink = formValuesToBeSent.updateLink ?? ({} as UpdateLink);
    formValuesToBeSent.updateLink.useWhatsapp = true;
    const missingSelections: string[] = [];
    if (!portfolioKey) {
      missingSelections.push("Portfolio");
    }
    if (!selectedManagementOption) {
      missingSelections.push("Management Option");
    }

    if (missingSelections?.length) {
      throw Error(`missing data ${missingSelections.join(",")}`)
    }

    if (formValuesToBeSent.service.portfolioOpeningRequest) {
      formValuesToBeSent.service.portfolioOpeningRequest.portfolio_db_id = portfolioKey ?? "";
      formValuesToBeSent.service.portfolioOpeningRequest.portfolioType = selectedManagementOption?.portfolio_type as PortfolioType || "";

      // Use the fees from form values (which may have been edited by user)
      formValuesToBeSent.service.portfolioOpeningRequest.fees = formValuesToBeSent.service.portfolioOpeningRequest.fees ?? selectedManagementOption?.fees ?? 0;

      formValuesToBeSent.service.portfolioOpeningRequest.assetClass = selectedManagementOption?.asset_class ?? "";

      if (existingPortfolio) {
        formValuesToBeSent.service.portfolioOpeningRequest.isSubPortfolio = true;
      }
    } else {

      throw Error(`missing portfolio opening request data`)

    }
    return formValuesToBeSent;
  }

  return (
    <Stack>
      <Title>Add Portfolio</Title>
      <Card shadow="sm" p="xl" radius="md" withBorder>

        <Container>

          <BaseServiceForm
            form={form}
            successRedirectPath="portfolios"
            preSubmitFormValues={preSubmitFormValues}
            serviceTypes={["portfolio"]}
            children={
              <>
                {/*  <Divider my={30} style={{ width: "100%" }} /> */}
                <Grid>
                  <Grid.Col span={{ xs: 24, lg: 24 }}>

                    <Text
                      variant="gradient"
                      gradient={{ from: "indigo", to: "cyan", deg: 45 }}
                      style={{ fontFamily: 'Greycliff CF, sans-serif' }}
                      //   ta="center"
                      fz="xl"
                      fw={700}
                    >
                      Portfolio Details
                    </Text>
                  </Grid.Col>

                  {/* Portfolio-specific fields */}
                  <>
                    <Grid.Col span={{ xs: 3 }}>
                      <TextInput
                        label="Portfolio ID"
                        placeholder="Enter portfolio ID (Optional)"
                        {...form.getInputProps("service.portfolioOpeningRequest.portfolioId")}
                      />
                    </Grid.Col>
                    {/*  Portfolio Manager select */}
                    <Grid.Col span={{ xs: 6 }}>

                      <GenevaApiSelect
                        key={"Manager"}
                        linkTable={"Manager"}
                        label={"Manager"}
                        // value={portfolioManager}
                        {...form.getInputProps("service.portfolioOpeningRequest.portfolioManager")}
                        onSelectItem={() => null}
                        disabled={false}
                        // required={true}
                        placeholder={"Select Portfolio Manager"}
                        showApiLabel={true}
                      />
                    </Grid.Col>

                    {/* 1. Portfolio select */}
                    <Grid.Col span={{ xs: 3 }}>
                      <Select
                        label="Portfolio"
                        placeholder="Select portfolio"
                        data={portfolioSelectData}
                        value={portfolioKey}
                        onChange={handlePortfolioChange}
                        searchable
                        nothingFoundMessage="No portfolios"
                      />
                    </Grid.Col>

                    {/* 2. Management option select (only if more than one choice) */}
                    {portfolioObj && shouldShowManagementSelect && (
                      <Grid.Col span={{ xs: 3 }}>
                        <Select
                          label="Management option"
                          placeholder="Select management style"
                          data={managementOptionSelectData}
                          value={managementOptionType}
                          onChange={handleManagementOptionChange}
                          searchable
                          nothingFoundMessage="No options"
                          disabled={!portfolioKey}
                        />
                      </Grid.Col>
                    )}
                  </>

                </Grid>


                {selectedManagementOption && (
                  <>
                    <Card withBorder mt="md" p="md">
                      <Text fw={500} mb="md">Portfolio Opening Details</Text>
                      <Divider />
                      <br />
                      {selectedManagementOption?.asset_class && (
                        <Text fw={500} mb="md">Asset Class: {selectedManagementOption?.asset_class}</Text>
                      )}
                      <Grid>
                        {selectedManagementOption?.fees !== undefined && (
                          <Grid.Col span={{ xs: 4 }}>
                            <TextInput
                              label="Portfolio fees"
                              type="number"
                              placeholder="Enter portfolio fees"
                              {...form.getInputProps("service.portfolioOpeningRequest.fees")}
                              onChange={(event) => {
                                // Mark as manually edited
                                setFeesManuallyEdited(true);
                                // Update the form value
                                form.setFieldValue("service.portfolioOpeningRequest.fees", parseFloat(event.currentTarget.value) || 0);
                              }}
                            />
                          </Grid.Col>
                        )}
                      </Grid>
                    </Card>

                    {existingPortfolio && (
                      <Card withBorder mt="md" p="md" bg="yellow.1">
                        <Text fw={500} mb="md">Existing Portfolio Details</Text>
                        <Divider />
                        <br />
                        <Text fw={500} mb="md">Portfolio Code: {existingPortfolio.code}</Text>
                        <Text fw={500} mb="md">Portfolio Type: {existingPortfolio.portfolioTypeCode}</Text>
                        <Text fw={500} mb="md">Portfolio Status: {existingPortfolio.status}</Text>
                        <Text fw={500} mb="md">Manager Name: {existingPortfolio.managerName}</Text>
                        <Text fw={500} mb="md">Risk Profile: {existingPortfolio.riskProfile}</Text>
                      </Card>
                    )}

                  </>
                )}
                <Divider my={"xl"} />

                <Grid >
                  <Grid.Col span={{ xs: 3 }}>
                    <Checkbox
                      checked={form.values.service.manual}
                      disabled={false}
                      onChange={(e) => {
                        form.setFieldValue("service.manual", e.target.checked);
                        form.setFieldValue("manual", e.target.checked);

                      }}


                      label="Manual Entry"
                    />
                  </Grid.Col>
                </Grid>
              </ >
            }

          />
        </Container>

      </Card >
    </Stack >
  );
}

export default AddPortfolio;
